import React, { useEffect, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import DatePicker from 'react-date-picker';
import {
  IProfession,
  ISingleProfession,
  ISingleSubProfession,
  ISingleSubSubProfession,
} from './constants';
import { SelectPanel } from './SelectPanel';
import { useProfessions } from '../../../../hooks/useProfessions';
import { IconCheck, IconCross, IconPlus } from '../../../../utils/icons';
import styles from './datepicker.module.css';

interface Props {
  profession?: IProfession;
  onChange: (profession: IProfession) => void;
  onCross?: () => void;
  inputProps?: any;
  onAddToPending?: (profession: IProfession) => void;
}

export default forwardRef<{ getCurrentValue: () => IProfession | null; clear: () => void }, Props>(
  function SingleProfessionInput({
    profession: professionProp,
    onChange,
    onCross,
    inputProps,
    onAddToPending,
    ...rest
  }, ref) {
    const { professionsList, subProfessionLibrary, areaOfFocusLibrary } = useProfessions();
    const [profession, setProfession] = useState<ISingleProfession>();
    const [subProfession, setSubProfession] = useState<ISingleSubProfession>();
    const [subSubProfession, setSubSubProfession] = useState<ISingleSubSubProfession>();
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [isActive, setIsActive] = useState<boolean>(true);
    const [showInputPanel, setShowInputPanel] = useState(true);

    const calendarClassName = styles['date-picker-calendar-container'];

    // Builds the IProfession output, or null when incomplete. Profession + start
    // date are required, and a non-"to present" entry needs an end date on/after
    // the start date — mirroring the pro_professions CHECK constraint so we never
    // POST a row the API will reject.
    const buildOutput = (): IProfession | null => {
      if (!profession || !startDate) return null;
      if (!isActive && !endDate) return null;
      if (!isActive && endDate && endDate < startDate) return null;
      return {
        profession: {
          id: profession.id,
          name: profession.name,
        },
        subProfession: subProfession
          ? {
              id: subProfession.id,
              professionId: profession.id,
              ProfessionId: profession.id,
              name: subProfession.name,
            }
          : null,
        subSubProfession: subSubProfession
          ? {
              id: subSubProfession.id,
              professionId: profession.id,
              name: subSubProfession.name,
            }
          : null,
        startDate: startDate.toISOString(),
        endDate: isActive ? null : endDate?.toISOString() ?? null,
        isActive,
      };
    };

    const resetFields = () => {
      setProfession(undefined);
      setSubProfession(undefined);
      setSubSubProfession(undefined);
      setStartDate(null);
      setEndDate(null);
      setIsActive(true);
      setShowInputPanel(false);
      setTimeout(() => setShowInputPanel(true), 10);
    };

    useImperativeHandle(ref, () => ({
      getCurrentValue: () => buildOutput(),
      clear: resetFields,
    }));

    useEffect(() => {
      if (professionProp) {
        setProfession(professionProp.profession);
        setSubProfession(professionProp.subProfession || undefined);
        setSubSubProfession(professionProp.subSubProfession || undefined);
        setStartDate(professionProp.startDate ? new Date(professionProp.startDate) : null);
        setEndDate(professionProp.endDate ? new Date(professionProp.endDate) : null);
        // Default to "to present" unless an explicit end date / isActive=false says otherwise.
        setIsActive(professionProp.isActive ?? !professionProp.endDate);
      }
    }, [professionProp]);

    const onProfessionChange = (value: ISingleProfession) => {
      setProfession(value);
      setSubProfession(undefined);
      setSubSubProfession(undefined);
    };

    const subProfessionList = useMemo(() => {
      const index = profession?.id || -1;
      return subProfessionLibrary[index] || [];
    }, [profession, subProfessionLibrary]);

    // Areas of Focus are L1-scoped, so the list depends on the selected
    // profession (not the sub-profession).
    const areaOfFocusList = useMemo(() => {
      const index = profession?.id || -1;
      return areaOfFocusLibrary[index] || [];
    }, [profession, areaOfFocusLibrary]);

    const onSubProfessionChange = (value: ISingleSubProfession) => {
      if (profession) setSubProfession(value);
    };

    const onSubSubProfessionChange = (value: ISingleSubSubProfession) => {
      if (profession) setSubSubProfession(value);
    };

    const onTogglePresent = (checked: boolean) => {
      setIsActive(checked);
      if (checked) setEndDate(null);
    };

    const canSubmit = !!buildOutput();

    const onSubmit = () => {
      const output = buildOutput();
      if (!output) return;
      if (onAddToPending) {
        onAddToPending(output);
      } else {
        onChange(output);
      }
      resetFields();
    };

    return (
      <Box
        {...rest}
        {...inputProps}
        className="flex flex-col gap-3 p-3 sm:p-4 border border-border-token rounded-lg"
      >
        {showInputPanel && (
          <>
            <Box className="flex gap-2 items-stretch sm:items-end flex-col sm:flex-row">
              <Box className="flex gap-2 flex-col w-full min-w-0">
                <Typography variant="body1" color="var(--text)">
                  Profession
                </Typography>
                <SelectPanel
                  value={profession}
                  onChange={onProfessionChange}
                  placeholder="Select profession"
                  objectOptions={professionsList.map((prof) => ({
                    label: prof.name,
                    value: prof,
                  }))}
                />
              </Box>
              <Box className="flex gap-2 flex-col w-full min-w-0">
                <Typography variant="body1" color="var(--text)">
                  Sub-category
                </Typography>
                <SelectPanel
                  value={subProfession}
                  onChange={onSubProfessionChange}
                  placeholder="Select sub-category"
                  objectOptions={subProfessionList.map((prof: any) => ({
                    label: prof.name,
                    value: prof,
                  }))}
                  disabled={!profession}
                />
              </Box>
              <Box className="flex gap-2 flex-col w-full min-w-0">
                <Typography variant="body1" color="var(--text)">
                  Area of focus
                </Typography>
                <SelectPanel
                  value={subSubProfession}
                  onChange={onSubSubProfessionChange}
                  placeholder="Select area of focus"
                  objectOptions={areaOfFocusList.map((aof: any) => ({
                    label: aof.name,
                    value: aof,
                  }))}
                  disabled={!profession || areaOfFocusList.length === 0}
                />
              </Box>
            </Box>

            <Box className="flex gap-2 items-end justify-between flex-col sm:flex-row">
              <Box className="flex gap-2 w-full min-w-0 flex-col sm:flex-row sm:items-end">
                <Box className="flex gap-2 flex-col w-full min-w-0">
                  <Typography variant="body1" color="var(--text)">
                    Start date
                  </Typography>
                  <DatePicker
                    format="MM/dd/yyyy"
                    value={startDate}
                    className="h-[40px] rounded-xl bg-surface-2"
                    calendarClassName={calendarClassName}
                    onChange={(value: Date) => setStartDate(value)}
                  />
                </Box>
                <Box className="flex gap-2 flex-col w-full min-w-0">
                  <Typography variant="body1" color="var(--text)">
                    End date
                  </Typography>
                  <DatePicker
                    format="MM/dd/yyyy"
                    value={endDate}
                    disabled={isActive}
                    className="h-[40px] rounded-xl bg-surface-2"
                    calendarClassName={calendarClassName}
                    onChange={(value: Date) => setEndDate(value)}
                  />
                </Box>
                <FormControlLabel
                  className="shrink-0"
                  control={
                    <Checkbox
                      checked={isActive}
                      onChange={(e) => onTogglePresent(e.target.checked)}
                      sx={{
                        color: 'var(--border)',
                        '&.Mui-checked': { color: 'var(--accent)' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="var(--text)" className="whitespace-nowrap">
                      To present
                    </Typography>
                  }
                />
              </Box>

              <Box className="flex gap-2 items-center justify-end p-1 self-end">
                {!onCross ? (
                  <Box
                    className={`flex items-center justify-center rounded-lg ${
                      canSubmit
                        ? 'bg-accent cursor-pointer hover:bg-[#8e682c]'
                        : 'bg-surface-2 cursor-not-allowed'
                    }`}
                    onClick={canSubmit ? onSubmit : undefined}
                  >
                    <IconPlus stroke="#fff" width={36} height={36} />
                  </Box>
                ) : (
                  <Box className="flex items-center justify-center gap-2">
                    <Box
                      className={`rounded-full p-1 ${
                        canSubmit
                          ? 'bg-accent cursor-pointer hover:bg-[#8e682c]'
                          : 'bg-surface-2 cursor-not-allowed'
                      }`}
                      onClick={canSubmit ? onSubmit : undefined}
                    >
                      <IconCheck stroke="#fff" width={30} height={30} />
                    </Box>
                    <Box
                      className="bg-surface border border-text-1 text-text-1 rounded-full cursor-pointer hover:bg-surface-2 p-1"
                      onClick={onCross}
                    >
                      {/* stroke is an SVG attribute (no var()); inherit the token via currentColor */}
                      <IconCross stroke="currentColor" width={28} height={28} />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  });
